import { useAuth } from "@/contexts/AuthContext";
import { useCredits } from "@/hooks/useCredits";
import { useTheme } from "@/contexts/ThemeContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LogOut, User, Zap, Crown, Building2, Moon, Sun } from "lucide-react";

export function UserMenu() {
  const { user, profile, signOut } = useAuth();
  const { credits, displayRemaining, displayLimit, planLabel, isLoading } = useCredits();
  const { theme, toggleTheme } = useTheme();

  if (!user) return null;

  const displayName = profile?.display_name || user.email?.split("@")[0] || "Utilisateur";
  const initial = displayName.charAt(0).toUpperCase();

  const PlanIcon = credits?.plan_type === 'business' ? Building2
    : credits?.plan_type === 'pro' ? Crown
      : Zap;

  const isLow = displayLimit > 0 && displayRemaining / displayLimit < 0.2;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2 px-2 py-1.5 transition-colors hover:bg-secondary">
          <span className="flex h-7 w-7 items-center justify-center bg-secondary text-[12px] font-medium text-foreground">
            {initial}
          </span>
          <span className="text-[13px] text-foreground">Mon compte</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56 border-border bg-popover shadow-none" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-[13px] font-medium leading-none text-foreground">{displayName}</p>
            <p className="font-mono text-[11px] leading-none text-faint">{user.email}</p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        <div className="px-2 py-1.5">
          <div className="flex items-center justify-between">
            <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-faint">Plan</span>
            <span className="flex items-center gap-1.5 text-[13px] font-medium text-foreground">
              <PlanIcon className="h-3.5 w-3.5 text-foreground" />
              {planLabel}
            </span>
          </div>
          {!isLoading && credits && (
            <div className="mt-2 flex items-center justify-between gap-2">
              <span className="font-mono text-[11px] text-faint">
                Crédits {credits.plan_type === 'free' ? 'jour' : 'mois'}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-1 w-6 bg-surface-3" aria-hidden="true">
                  <span
                    className={`block h-full ${isLow ? 'bg-err' : 'bg-foreground'}`}
                    style={{
                      width: `${Math.max(4, Math.min(100, (displayRemaining / displayLimit) * 100))}%`,
                    }}
                  />
                </span>
                <span className={`font-mono text-[11px] ${isLow ? 'text-err' : 'text-muted-foreground'}`}>
                  {displayRemaining}/{displayLimit}
                </span>
              </span>
            </div>
          )}
        </div>

        <DropdownMenuSeparator />
        <DropdownMenuItem className="cursor-pointer" onClick={toggleTheme}>
          {theme === 'light' ? <Moon className="mr-2 h-4 w-4" /> : <Sun className="mr-2 h-4 w-4" />}
          {theme === 'light' ? 'Thème sombre' : 'Thème clair'}
        </DropdownMenuItem>
        <DropdownMenuItem className="cursor-pointer" disabled>
          <User className="mr-2 h-4 w-4" />
          Mon compte
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="cursor-pointer text-err focus:text-err"
          onClick={signOut}
        >
          <LogOut className="mr-2 h-4 w-4" />
          Déconnexion
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
